import {
  AGENT_TIER_COMMISSION_PERCENT,
  AGENT_TIER_LABEL_KEYS,
  AGENT_TIER_REFERRAL_PERCENT,
  PROGRESSION_TIER_RANGE_AED,
  TIER_ORDER,
} from '~/constants/agentTiers';
import type { CommissionLadderTier, CommissionTiersData } from '~/types/commissionTiers';

const THOUSAND = 1_000;
const MILLION = 1_000_000;

const trimDecimals = (n: number) => String(Math.round(n * 100) / 100);

// 0 → "0", 100000 → "100K", 1500000 → "1.5M"
export const compactAed = (amount: number): string => {
  if (amount >= MILLION) return `${trimDecimals(amount / MILLION)}M`;
  if (amount >= THOUSAND) return `${trimDecimals(amount / THOUSAND)}K`;
  return String(amount);
};

// Upper bound is stored inclusive (next minimum − 1); the ladder shows the next milestone: "2M – 3M", "3M+".
export const formatTierRange = (minAed: number, maxAed: number | null): string =>
  maxAed == null ? `${compactAed(minAed)}+` : `${compactAed(minAed)} – ${compactAed(maxAed + 1)}`;

export const tierPayoutAed = (dealCommissionAed: number, percent: number): number =>
  Math.round((dealCommissionAed * percent) / 100);

export const isCommissionTiersData = (value: unknown): value is CommissionTiersData => {
  const v = value as Partial<CommissionTiersData> | null | undefined;
  return (
    !!v &&
    Array.isArray(v.ladder) &&
    v.ladder.length > 0 &&
    v.ladder.every((r) => typeof r?.tierKey === 'string' && Number.isFinite(r?.selfClosePercent))
  );
};

// Portal-only fallback while the tiers endpoint loads. The public calculator never uses it.
export const constantsLadder = (): CommissionLadderTier[] =>
  TIER_ORDER.map((tierKey, i) => ({
    tierKey,
    sortOrder: i + 1,
    minCommissionAed: PROGRESSION_TIER_RANGE_AED[tierKey].min,
    maxCommissionAed: PROGRESSION_TIER_RANGE_AED[tierKey].maxInclusive,
    selfClosePercent: AGENT_TIER_COMMISSION_PERCENT[tierKey],
    referralPercent: AGENT_TIER_REFERRAL_PERCENT[tierKey],
    labelKey: AGENT_TIER_LABEL_KEYS[tierKey],
  }));
